const API_BASE = '/api'

async function authFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Session expired')
  }
  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

function workspacePath(workspaceKey: string, suffix = ''): string {
  if (!workspaceKey) throw new Error('Workspace is required')
  return `/workspaces/${encodeURIComponent(workspaceKey)}${suffix}`
}

export interface ApiThesis {
  id: string
  org_id: string
  name: string
  status: string
  seed: unknown
  decomposition: unknown
  killed_reason: string | null
  graduated_at: string | null
  trading_mode: string
  created_at: string
}

export interface ApiRun {
  run_id: string
  parent_run_id: string | null
  thesis_id: string | null
  agent_name: string
  agent_commit_sha: string | null
  run_mode: string
  event_id: string
  status: string
  output: unknown
  usage: unknown
  caveman_summary: string | null
  error: string | null
  started_at: string
  closed_at: string | null
}

export interface ApiSpan {
  span_id: string
  run_id: string
  parent_span_id: string | null
  sequence_id: number
  kind: string
  name: string
  input: unknown
  output: unknown
  is_error: boolean
  error: string | null
  started_at: string
  ended_at: string | null
}

export interface ApiDecision {
  decision_id: string
  run_id: string
  thesis_id: string
  decision_type: string
  belief: string
  evidence: unknown
  action: string
  expected: unknown
  created_at: string
}

export interface ApiEvent {
  id: string
  org_id: string
  thesis_id: string | null
  workspace_id: string | null
  user_id: string | null
  event_type: string
  payload: unknown
  created_at: string
  acked_at: string | null
}

export interface ApiOrder {
  id: string
  thesis_id: string | null
  product: string
  contract_code: string
  side: string
  limit_price_pln: number | null
  size_mwh: number | null
  status: string
  execution_mode: string
  paper_group_id: string | null
  created_by: string
  submitted_at: string
  expires_at: string | null
  cancelled_at: string | null
}

export interface ApiFill {
  id: string
  order_id: string
  account_id: string
  fill_price_pln: number | null
  fill_size_mwh: number | null
  filled_at: string
  source_trade_id: string | null
  bucket_id: string | null
}

export interface ApiDataSource {
  name: string
  table: string
  bitemporal: boolean
  row_count: number | null
}

export interface ApiEventRow extends ApiEvent {
  thesis_name: string | null
  run_id: string | null
}

export interface ApiRunRow {
  run_id: string
  parent_run_id: string | null
  thesis_id: string | null
  thesis_name: string | null
  agent_name: string
  agent_commit_sha: string | null
  run_mode: string
  event_id: string
  status: string
  caveman_summary: string | null
  error: string | null
  started_at: string
  closed_at: string | null
}

export interface ApiAgent {
  name: string
  workspace_key?: string
  workspace_name?: string
  prompt: string
  model: string
  run_mode: string
  tool_allowlist: string[]
  skill_allowlist: string[]
  userskills: boolean
  max_turns: number
  effort: string
  file: string
}

export interface ApiThesisDetail {
  thesis: ApiThesis
  runs: ApiRun[]
  decisions: ApiDecision[]
  events: ApiEvent[]
}

export interface ApiRunSpans {
  run: ApiRun
  spans: ApiSpan[]
}

export const fetchTheses = () =>
  authFetch<{ theses: ApiThesis[] }>('/theses').then((r) => r.theses)

export const fetchThesis = (id: string) => authFetch<ApiThesisDetail>(`/theses/${id}`)

export const fetchRunSpans = (workspaceKey: string, runId: string) =>
  authFetch<ApiRunSpans>(workspacePath(workspaceKey, `/runs/${runId}/spans`))

export const fetchThesisOrders = (id: string) =>
  authFetch<{ orders: ApiOrder[] }>(`/theses/${id}/orders`).then((r) => r.orders)

export const fetchThesisFills = (id: string) =>
  authFetch<{ fills: ApiFill[] }>(`/theses/${id}/fills`).then((r) => r.fills)

export const fetchEvents = () =>
  authFetch<{ events: ApiEventRow[] }>('/events').then((r) => r.events)

export const fetchRuns = (workspaceKey: string) =>
  authFetch<{ runs: ApiRunRow[] }>(workspacePath(workspaceKey, '/runs')).then((r) => r.runs)

export const fetchAgents = (workspaceKey: string) =>
  authFetch<{ agents: ApiAgent[] }>(workspacePath(workspaceKey, '/agents')).then((r) => r.agents)

export const fetchAgent = (workspaceKey: string, name: string) =>
  authFetch<ApiAgent>(workspacePath(workspaceKey, `/agents/${encodeURIComponent(name)}`))

export interface ApiCron {
  id: string
  agent_name: string
  user_id: string | null
  created_by: string | null
  can_edit: boolean
  frequency: string
  hour: number
  minute: number
  day_of_week: number | null
  day_of_month: number | null
  timezone: string
  enabled: boolean
  next_run_at: string | null
  last_run_at: string | null
}

export const fetchCrons = (workspaceKey: string) =>
  authFetch<{ crons: ApiCron[] }>(workspacePath(workspaceKey, '/crons'))

export interface ApiLoop {
  id: string
  agent_name: string
  user_id: string | null
  created_by: string | null
  can_edit: boolean
  frequency: string
  hour: number
  minute: number
  day_of_week: number | null
  day_of_month: number | null
  timezone: string
  max_runs: number
  max_tokens: number | null
  idle_break: boolean
  enabled: boolean
  status: 'idle' | 'active'
  runs_done: number
  tokens_used: number
  last_break_reason: string | null
  next_run_at: string | null
  last_run_at: string | null
}

export const fetchLoops = (workspaceKey: string) =>
  authFetch<{ loops: ApiLoop[] }>(workspacePath(workspaceKey, '/loops'))

export const fetchDataSources = () =>
  authFetch<{ data_sources: ApiDataSource[] }>('/data-sources').then((r) => r.data_sources)

export const fetchDbSample = (source: string, n = 100) =>
  authFetch<{ rows: Record<string, unknown>[]; n_rows: number }>(
    `/db/sample?source=${encodeURIComponent(source)}&n=${n}`,
  )
